interface ChainLink {
  id: string;
  supersedesLineId: string | null;
  supersededByLineId: string | null;
}

/**
 * Every version of one price, oldest to newest, found by following `supersedesLineId` back and
 * `supersededByLineId` forward through `lines`. A pointer to a line that isn't in `lines` ends the walk there,
 * and a cycle (which the database can't produce) is cut rather than looped on.
 */
export function buildLineHistoryChain<L extends ChainLink>(line: L, lines: L[]): L[] {
  const byId = new Map(lines.map((candidate) => [candidate.id, candidate]));
  byId.set(line.id, line);
  const seen = new Set([line.id]);

  const older: L[] = [];
  for (let at = line; at.supersedesLineId;) {
    const previous = byId.get(at.supersedesLineId);
    if (!previous || seen.has(previous.id)) break;
    seen.add(previous.id);
    older.unshift(previous);
    at = previous;
  }

  const newer: L[] = [];
  for (let at = line; at.supersededByLineId;) {
    const next = byId.get(at.supersededByLineId);
    if (!next || seen.has(next.id)) break;
    seen.add(next.id);
    newer.push(next);
    at = next;
  }

  return [...older, line, ...newer];
}
